import * as React from 'react';

interface TriggerRefContextValue {
	triggerRef: React.RefObject<HTMLElement>;
	setTriggerElement: (element: HTMLElement | null) => void;
}

const TriggerRefContext = React.createContext<TriggerRefContextValue | null>(null);

/**
 * Provider component that manages the trigger element ref for the customize sidebar.
 * This allows the Customise button (in the header) to share its ref with the sidebar
 * (in the main content area) for proper focus management.
 */
export const CustomiseSidebarTriggerProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const triggerRef = React.useRef<HTMLElement>(null);

	const setTriggerElement = React.useCallback((element: HTMLElement | null) => {
		// Using a mutable ref pattern since we can't reassign .current on a RefObject
		(triggerRef as React.MutableRefObject<HTMLElement | null>).current = element;
	}, []);

	const value = React.useMemo(
		() => ({
			triggerRef,
			setTriggerElement,
		}),
		[setTriggerElement],
	);

	return <TriggerRefContext.Provider value={value}>{children}</TriggerRefContext.Provider>;
};

/**
 * Hook to get the trigger ref for the customize sidebar.
 * Used by the sidebar component to know where to return focus when closed.
 */
export const useCustomiseSidebarTriggerRef = (): React.RefObject<HTMLElement> | undefined => {
	const context = React.useContext(TriggerRefContext);
	return context?.triggerRef;
};

/**
 * Hook to set the trigger element for the customize sidebar.
 * Used by the Customise button to register itself as the trigger.
 */
export const useSetCustomiseSidebarTrigger = ():
	| ((element: HTMLElement | null) => void)
	| undefined => {
	const context = React.useContext(TriggerRefContext);
	return context?.setTriggerElement;
};
